import { useCallback } from "react";
import { Settings01 } from "@untitledui/icons";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import type { NavAccountMenuItem } from "@/components/application/app-navigation/base-components/nav-account-card";
import { LocaleSelect } from "@/components/app/LocaleSelect";
import { ThemeToggle } from "@/components/app/ThemeToggle";
import { accountDisplay, currentUser, type AccountDisplay } from "@/lib/auth";
import { ettsBootstrap } from "@/lib/bootstrap";

/**
 * Obsah menu účtu — jeden zdroj pro obě místa, kde se menu otevírá: kartu
 * v patičce sidebaru a avatara v horní liště. Kdyby si každé skládalo své,
 * rozejdou se při první další položce.
 */
export interface AccountMenuContent {
  display: AccountDisplay | null;
  menuTop: React.ReactNode;
  menuItems: NavAccountMenuItem[];
  onSignOut: () => void;
}

/**
 * Odhlášení je form POST na `/admin/logout` (web chain), ne `apiFetch`:
 * Spring Security očekává formulářový požadavek s CSRF tokenem, odpovídá
 * redirectem a při té příležitosti odvolá JWT jti a smaže cookie `etts_access`.
 *
 * Formulář se staví imperativně místo skrytého `<form>` v JSX, protože obsah
 * sidebaru se renderuje dvakrát (mobilní modál + fixní desktop) — dva formuláře
 * se stejným `ref` by si přebíjely referenci.
 */
export function useSignOut(): () => void {
  const csrfToken = ettsBootstrap().csrfToken;

  return useCallback(() => {
    const form = document.createElement("form");
    form.method = "post";
    form.action = "/admin/logout";
    form.style.display = "none";

    if (csrfToken) {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = "_csrf";
      input.value = csrfToken;
      form.appendChild(input);
    }

    document.body.appendChild(form);
    form.submit();
  }, [csrfToken]);
}

export function useAccountMenu(): AccountMenuContent {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const onSignOut = useSignOut();
  const user = currentUser();

  // Jazyk jako výběr (seznam poroste), motiv jako segmentový přepínač — v menu je
  // místo na všechny tři volby včetně „Systém", které se do lišty nevejdou.
  const menuTop = (
    <div className="flex flex-col gap-2 p-2">
      <LocaleSelect />
      <ThemeToggle />
    </div>
  );

  const menuItems: NavAccountMenuItem[] = [
    {
      label: t("nav.settings"),
      icon: Settings01,
      onClick: () => void navigate({ to: "/settings" }),
    },
  ];

  return { display: user ? accountDisplay(user) : null, menuTop, menuItems, onSignOut };
}
